import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { LoadingSpinner } from '../components'
import { Button, Card } from '../components/ui'
import { API_BASE_URL, LoadingSpinnerMeta } from '../data'
import type { PublicForm, PublicFormField, PublicFormFillMetaProps } from '../data/types'

type Props = {
    meta: PublicFormFillMetaProps
}

type Answers = Record<string, string | string[]>

const parseError = async (response: Response) => {
    const text = await response.text()
    try {
        const parsed = JSON.parse(text)
        if (parsed.errors) {
            return Object.values(parsed.errors).flat().join(' ')
        }
        return parsed.message || text
    } catch {
        return text
    }
}

const initialAnswers = (fields: PublicFormField[]): Answers => {
    return fields.reduce<Answers>((answers, field) => {
        answers[field.id || field.label] = field.type === 'checkbox' ? [] : ''
        return answers
    }, {})
}

export const PublicFormFill = (props: Props) => {
    const { meta } = props
    const { slug } = useParams()
    const [form, setForm] = useState<PublicForm | null>(null)
    const [answers, setAnswers] = useState<Answers>({})
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const fetchForm = async () => {
            setLoading(true)
            setError(null)
            try {
                const response = await fetch(`${API_BASE_URL}/public-forms/${slug}`)
                if (!response.ok) {
                    throw new Error(response.status === 404 ? meta.notFoundMessage : await parseError(response))
                }
                const result = await response.json()
                setForm(result)
                setAnswers(initialAnswers(result.fields || []))
            } catch (err) {
                setError(err instanceof Error ? err.message : meta.notFoundMessage)
            } finally {
                setLoading(false)
            }
        }

        fetchForm()
    }, [slug, meta.notFoundMessage])

    const updateAnswer = (field: PublicFormField, value: string | string[]) => {
        setAnswers(prev => ({ ...prev, [field.id || field.label]: value }))
    }

    const toggleCheckboxAnswer = (field: PublicFormField, option: string) => {
        const key = field.id || field.label
        const currentValue = answers[key]
        const currentAnswers = Array.isArray(currentValue) ? currentValue : []
        const nextAnswers = currentAnswers.includes(option)
            ? currentAnswers.filter(value => value !== option)
            : [...currentAnswers, option]

        updateAnswer(field, nextAnswers)
    }

    const submitForm = async (event: React.FormEvent) => {
        event.preventDefault()
        if (!form) return

        setSubmitting(true)
        setError(null)
        try {
            const response = await fetch(`${API_BASE_URL}/public-forms/${form.slug}/submissions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ answers })
            })

            if (!response.ok) {
                throw new Error(await parseError(response))
            }

            setSubmitted(true)
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorTitle)
        } finally {
            setSubmitting(false)
        }
    }

    const renderField = (field: PublicFormField) => {
        const key = field.id || field.label
        const value = answers[key]
        const label = (
            <span>
                {field.label}
                {field.required && <span className="ms-2 text-danger-600 dark:text-danger-400 text-sm">{meta.requiredLabel}</span>}
            </span>
        )

        if (field.type === 'text' || field.type === 'email') {
            return (
                <label key={key} className="block space-y-2">
                    <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
                    <input type={field.type === 'email' ? 'email' : 'text'} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-gray-900 dark:text-gray-100" value={typeof value === 'string' ? value : ''} onChange={event => updateAnswer(field, event.target.value)} />
                </label>
            )
        }

        if (field.type === 'select') {
            return (
                <label key={key} className="block space-y-2">
                    <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
                    <select className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-gray-900 dark:text-gray-100" value={typeof value === 'string' ? value : ''} onChange={event => updateAnswer(field, event.target.value)}>
                        <option value="">{meta.selectPlaceholder}</option>
                        {field.options.map(option => <option key={option} value={option}>{option}</option>)}
                    </select>
                </label>
            )
        }

        if (field.type === 'radio') {
            return (
                <fieldset key={key} className="space-y-3">
                    <legend className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</legend>
                    {field.options.map(option => (
                        <label key={option} className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                            <input type="radio" name={key} value={option} checked={value === option} onChange={() => updateAnswer(field, option)} />
                            {option}
                        </label>
                    ))}
                </fieldset>
            )
        }

        return (
            <fieldset key={key} className="space-y-3">
                <legend className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</legend>
                {field.options.map(option => {
                    const currentAnswers = Array.isArray(value) ? value : []
                    return (
                        <label key={option} className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                            <input type="checkbox" checked={currentAnswers.includes(option)} onChange={() => toggleCheckboxAnswer(field, option)} />
                            {option}
                        </label>
                    )
                })}
            </fieldset>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 px-4 py-10">
            <div className="mx-auto max-w-3xl">
                {loading && (
                    <div className="flex justify-center items-center min-h-[400px]">
                        <LoadingSpinner meta={{ ...LoadingSpinnerMeta, loadingText: meta.loadingText }} />
                    </div>
                )}

                {!loading && error && (
                    <Card>
                        <div className="p-6 bg-danger-50 dark:bg-danger-900/30 text-danger-700 dark:text-danger-200">
                            <h1 className="text-2xl font-bold mb-2">{meta.errorTitle}</h1>
                            <p>{error}</p>
                        </div>
                    </Card>
                )}

                {!loading && !error && submitted && (
                    <Card>
                        <div className="p-8 text-center">
                            {/* IV Logo */}
                            <div className="flex justify-center mb-6">
                                <img
                                    src="/logo512.png"
                                    alt="IV Logo"
                                    className="h-16 w-auto object-contain"
                                />
                            </div>
                            {/* Success Icon */}
                            {/* <div className="flex justify-center mb-5">
                                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                                    <svg
                                        className="w-8 h-8 text-green-600"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M5 13l4 4L19 7"
                                        />
                                    </svg>
                                </div>
                            </div> */}
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">{meta.successTitle || "Thank You for Your Submission!"}</h1>
                            < p className="text-gray-600 dark:text-gray-400">{meta.successMessage || "We've successfully received your submission. Thank you for taking the time to submit this form."}</p>
                        </div>
                    </Card>
                )}

                {!loading && !error && !submitted && form && (
                    <Card>
                        <form onSubmit={submitForm} className="p-8 space-y-6">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{form.title}</h1>
                                {form.description && <p className="mt-3 text-gray-600 dark:text-gray-400">{form.description}</p>}
                            </div>
                            <div className="space-y-6">
                                {form.fields.map(renderField)}
                            </div>
                            <div className="pt-2">
                                <Button type="submit" disabled={submitting}>{submitting ? meta.submittingText : meta.submitButton}</Button>
                            </div>
                        </form>
                    </Card>
                )}
            </div>
        </div>
    )
}
